/**
 * 顧客管理（Excel）画面のセル色・コメント情報および変換ユーティリティ
 *
 * 各顧客（行）は cellMeta という JSON 文字列カラムを持ち、以下の形式でセル単位の
 * 背景色・コメントを保持する:
 *
 *   { "<元の列インデックス>": { "color"?: string, "comment"?: string } }
 *
 * 列インデックスは COLUMNS 配列（顧客管理（Excel）画面）の固定順（Orig_Index）を指す。
 * 表示上の列並び替え（columnOrder / Display_Order）とは独立して保存されるため、
 * ユーザーが列順を変更してもセルの色・コメントは正しい項目に紐付いたまま残る。
 */

export interface CellMetaEntry {
  color?: string;
  comment?: string;
}

export type CellMetaMap = Record<number, CellMetaEntry>;

/** 選択可能な背景色パレット（右クリックメニューの「セル色変更」用） */
export const CELL_COLOR_PALETTE: { label: string; value: string }[] = [
  { label: '赤', value: '#ffcdd2' },
  { label: '黄', value: '#fff9c4' },
  { label: '緑', value: '#c8e6c9' },
  { label: '青', value: '#bbdefb' },
  { label: 'グレー', value: '#e0e0e0' },
  { label: 'オレンジ', value: '#ffe0b2' },
  { label: '紫', value: '#e1bee7' },
  { label: '水色', value: '#b2ebf2' },
  { label: 'ピンク', value: '#f8bbd0' },
  { label: '黄緑', value: '#dcedc8' },
];

export function parseCellMeta(raw: string | null | undefined): CellMetaMap {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as CellMetaMap;
    }
  } catch {
    // ignore malformed JSON, treat as empty
  }
  return {};
}

export function serializeCellMeta(meta: CellMetaMap): string | null {
  const cleaned: CellMetaMap = {};
  for (const [key, entry] of Object.entries(meta)) {
    if (entry && (entry.color || entry.comment)) {
      cleaned[Number(key)] = entry;
    }
  }
  return Object.keys(cleaned).length > 0 ? JSON.stringify(cleaned) : null;
}

// ============================================================
// Spreadsheet cell-name <-> coordinate helpers
// ============================================================

/** 0-indexed column + 0-indexed row -> spreadsheet cell name (e.g. [0,0] -> "A1"). */
export function cellNameFromCoords(colIdx: number, rowIdx: number): string {
  let col = colIdx;
  let letters = '';
  if (col > 701) {
    letters += String.fromCharCode(64 + Math.floor(col / 676));
    letters += String.fromCharCode(64 + Math.floor((col % 676) / 26));
  } else if (col > 25) {
    letters += String.fromCharCode(64 + Math.floor(col / 26));
  }
  letters += String.fromCharCode(65 + (col % 26));
  return `${letters}${rowIdx + 1}`;
}

/** Extracts the `background-color` value from a CSS style string. */
export function extractBackgroundColor(styleStr: string | null | undefined): string | null {
  if (!styleStr) return null;
  const match = /background-color\s*:\s*([^;]+)/i.exec(styleStr);
  return match ? match[1].trim() : null;
}

// ============================================================
// Display_Order <-> Orig_Index mapping
// ============================================================

/** Display_Order 行 → Orig_Index 行。`order[displayIdx] = origIdx`。 */
export function displayToOriginalRow<T>(displayRow: T[], order: number[], columnCount: number): (T | null)[] {
  const originalRow: (T | null)[] = Array(columnCount).fill(null);
  for (let displayIdx = 0; displayIdx < order.length; displayIdx++) {
    const origIdx = order[displayIdx];
    if (origIdx >= 0 && origIdx < columnCount) originalRow[origIdx] = displayRow[displayIdx];
  }
  return originalRow;
}

/** 固定列を必ず先頭に置いた列順へ正規化する（保存済み order の読み込み直後に必ず通す）。 */
export function normalizeColumnOrder(order: number[], fixed: number[]): number[] {
  return [...fixed, ...order.filter((idx) => !fixed.includes(idx))];
}

// ============================================================
// Column state persistence (localStorage, per user)
// ============================================================

export interface ColumnSavedState {
  widths: number[];
  order: number[];
  hidden?: number[];
}

export function getColumnStateKey(userId: string): string {
  return `customer-excel-col-state-${userId}`;
}

export function saveColumnState(userId: string, state: ColumnSavedState): void {
  try {
    localStorage.setItem(getColumnStateKey(userId), JSON.stringify(state));
  } catch {
    // ignore storage failures
  }
}

export function loadColumnState(userId: string): ColumnSavedState | null {
  try {
    const stored = localStorage.getItem(getColumnStateKey(userId));
    return stored ? (JSON.parse(stored) as ColumnSavedState) : null;
  } catch {
    return null;
  }
}

/** 非表示列集合から固定列を除外する（固定列は常に表示状態を維持する）。 */
export function sanitizeHiddenColumns(hidden: Iterable<number> | null | undefined, fixed: number[]): Set<number> {
  const result = new Set<number>();
  if (!hidden) return result;
  for (const idx of hidden) {
    if (!fixed.includes(idx)) result.add(idx);
  }
  return result;
}
